// Converted from shared/design/components/SettingsNav.html by scripts/dc2tsx; edited by hand from here on.
import React, { Fragment } from 'react';
import { DCLogic } from '../ds/DCLogic';
import { each, ip, route, sx } from '../ds/tpl';
import { t } from '../i18n';

export interface SettingsNavProps {
  active?:
    | 'users'
    | 'roles'
    | 'channels'
    | 'profile'
    | 'mapping'
    | 'markup'
    | 'lpswap'
    | 'commission'
    | 'general'
    | 'health'
    | 'api'
    | 'templates'
    | 'audit'
    | (string & {});
  role?: 'owner' | 'risk' | 'dealer' | 'readonly' | 'no-access' | (string & {});
  demo?: any;
  /** live mode: items the session's permissions allow (instead of the role preset) */
  permissions?: string[];
  /** live badges: { health: '86%', markup: '38/42', lpswap: '2 stale' } */
  badges?: Record<string, string>;
  [key: string]: any;
}

/** Permission of each settings page (GET /auth/session permissions); profile is always visible. */
const SET_PERM: Record<string, string> = {
  general: 'page.settings.general',
  health: 'page.settings.setup_health',
  users: 'page.settings.users',
  roles: 'page.settings.roles',
  mapping: 'page.settings.mapping',
  markup: 'page.settings.costs',
  lpswap: 'page.settings.costs',
  commission: 'page.settings.costs',
  channels: 'page.settings.channels',
  templates: 'page.settings.email_templates',
  api: 'page.settings.data_api',
  audit: 'page.settings.audit',
};
/** AI & integrations: the settings view, or only "Connect AI apps" (tab My AI connection). */
const AI_PERMS = ['page.settings.ai', 'action.connect_ai'];

export class SettingsNav extends DCLogic<SettingsNavProps> {
  renderVals() {
    const p = this.props;
    const active = p.active ?? 'users';
    const role = p.role ?? 'owner';
    let groups = [
      {
        label: t('app.settings.eb-workspace', 'WORKSPACE'),
        items: [
          ['general', t('app.settings.general', 'General'), 'SET-09_general'],
          ['health', t('app.settings.health', 'Setup health'), 'SET-10_setup-health', '87%', 'bx-badge bx-badge--warn bx-badge--mono'],
        ],
      },
      {
        label: t('app.settings.eb-team-and-access', 'TEAM AND ACCESS'),
        items: [
          ['users', t('app.settings.users', 'Users'), 'SET-01_users'],
          ['roles', t('app.settings.roles', 'Roles and permissions'), 'SET-02_roles'],
          ['profile', t('app.settings.profile', 'My profile'), 'SET-04_profile'],
        ],
      },
      {
        label: t('app.settings.eb-trading-setup', 'TRADING SETUP'),
        items: [
          ['mapping', t('app.settings.mapping', 'Groups, books and symbols'), 'SET-05_book-mapping'],
          ['markup', t('app.settings.markup', 'Costs · Markup'), 'SET-06_costs-markup', '38/42', 'bx-badge bx-badge--warn bx-badge--mono'],
          ['lpswap', t('app.settings.lpswap', 'Costs · LP swap'), 'SET-07_costs-lp-swap', '2 stale', 'bx-badge bx-badge--warn'],
          ['commission', t('app.settings.commission', 'Costs · Commission'), 'SET-08_costs-commission'],
        ],
      },
      {
        label: t('app.settings.eb-notifications', 'NOTIFICATIONS'),
        items: [
          ['channels', t('app.settings.channels', 'Notification channels'), 'SET-03_notification-channels'],
          ['templates', t('app.settings.templates', 'Client email templates'), 'SET-12_email-templates'],
        ],
      },
      {
        label: t('app.settings.eb-integrations-and-audit', 'INTEGRATIONS AND AUDIT'),
        items: [
          ['api', t('app.settings.api', 'Data API keys'), 'SET-11_data-api'],
          ['ai', t('app.settings.ai', 'AI & integrations'), 'SET-14_ai-integrations'],
          ['audit', t('app.settings.audit', 'Audit log'), 'SET-13_audit-log'],
        ],
      },
    ];
    const personal = (ai: boolean) => [
      {
        label: t('app.settings.eb-personal', 'PERSONAL'),
        items: [
          ['profile', t('app.settings.profile', 'My profile'), 'SET-04_profile'],
          ...(ai ? [['ai', t('app.settings.ai', 'AI & integrations'), 'SET-14_ai-integrations']] : []),
        ],
      },
    ];
    const perms = p.permissions;
    if (perms) {
      const b = p.badges || {};
      const allowed = (id: string) =>
        id === 'profile' || (id === 'ai' ? AI_PERMS.some((x) => perms.includes(x)) : perms.includes(SET_PERM[id]));
      groups = groups
        .map((g) => ({
          label: g.label,
          items: g.items.filter(([id]) => allowed(id)).map(([id, label, file, , badgeCls]) => [id, label, file, b[id] || '', badgeCls]),
        }))
        .filter((g) => g.items.length);
      // a role that sees only its personal pages gets them under PERSONAL, as the design shows
      const ids = groups.flatMap((g) => g.items.map(([id]) => id));
      if (ids.every((id) => id === 'profile' || id === 'ai')) groups = personal(ids.includes('ai'));
    } else if (role === 'dealer' || role === 'readonly') {
      // a custom role without "Connect AI apps (MCP)" (Night dealer) loses AI & integrations
      groups = personal(true);
    } else if (role === 'no-access') groups = personal(false);
    return {
      wrap: p.demo ? 'padding: 12px; background: var(--surface-1); min-height: 540px;' : '',
      groups: groups.map((g) => ({
        label: g.label,
        items: g.items.map(([id, label, file, badge, badgeCls]) => ({
          label,
          href: file + '.html',
          cur: id === active ? 'page' : 'false',
          badge: badge || '',
          badgeCls: badgeCls || 'bx-badge',
        })),
      })),
    };
  }
  view(v: any) {
    return (
      <div style={sx(`font-family: var(--font-sans); color: var(--ink); ${v.wrap ?? ''}`)}>
        <nav className="bx-subnav" aria-label={t('c.settingsnav.settings', 'Settings')}>
          {each(v.groups, (g, $gI) => (
            <Fragment key={$gI}>
              <span className="bx-subnav-eb">{ip(g?.label)}</span>
              {each(g?.items, (it, $itI) => (
                <a key={$itI} className="bx-nav-it" href={route(it?.href)} aria-current={it?.cur}>
                  <span style={{ flex: '1', minWidth: '0' }}>{ip(it?.label)}</span>
                  {it?.badge ? <span className={it?.badgeCls}>{ip(it?.badge)}</span> : null}
                </a>
              ))}
            </Fragment>
          ))}
        </nav>
      </div>
    );
  }
}
